const LOCALE_SEGMENT_REGEX = /^[a-zA-Z]{2,3}(?:-[a-zA-Z0-9]{2,8})*-[a-zA-Z]{2}$/;

const splitPathSegments = (pathname: string) => pathname.split('/').filter(Boolean);

const findLocaleIndex = (segments: string[]) => segments.findIndex((segment) => LOCALE_SEGMENT_REGEX.test(segment));

export const parseLocaleFromPath = (pathname: string) => {
  const segments = splitPathSegments(pathname);
  const localeIndex = findLocaleIndex(segments);

  if (localeIndex < 0) {
    return {
      language: null,
      market: null,
      localeIndex,
      segments
    };
  }

  const locale = segments[localeIndex];
  const marketSeparatorIndex = locale.lastIndexOf('-');
  const language = locale.slice(0, marketSeparatorIndex);
  const market = locale.slice(marketSeparatorIndex + 1);

  return {
    language: language || null,
    market: market || null,
    localeIndex,
    segments
  };
};

const buildPath = (segments: string[]) => `/${segments.join('/')}`;

/* replaceLocaleInPath only updates an existing locale segment (language) in the url,
and returns null if no locale segment is present. This is used for market updates,
where we only want to update the market segment if a locale is already present in the url,
otherwise we would lose the language info on market change */
export const replaceLocaleInPath = (pathname: string, nextLocale: string) => {
  const { segments, localeIndex } = parseLocaleFromPath(pathname);
  if (localeIndex < 0) return null;

  const nextSegments = [...segments];
  nextSegments[localeIndex] = nextLocale;
  return buildPath(nextSegments);
};

/* upsertLocaleInPath updates when present, or inserts locale when missing, is more suitable for language updates,
where we want to add the language segment to the url even if it's not present yet. */
export const upsertLocaleInPath = (pathname: string, nextLocale: string) => {
  const { segments, localeIndex } = parseLocaleFromPath(pathname);

  if (localeIndex >= 0) {
    const nextSegments = [...segments];
    nextSegments[localeIndex] = nextLocale;
    return buildPath(nextSegments);
  }

  if (segments.length === 0) return `/${nextLocale}`;

  return buildPath([segments[0], nextLocale, ...segments.slice(1)]);
};

export const removeLocaleFromPath = (pathname: string) => {
  const segments = splitPathSegments(pathname);
  return buildPath(segments.filter((segment) => !LOCALE_SEGMENT_REGEX.test(segment)));
};
